/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { Component, EventEmitter, Injectable, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { IAddListService } from 'app/shared/interfaces/IAddListService.interface';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { Subscription } from 'rxjs';

// Acts as a mediator between the container and its children
// the create component will take control by passing in a negative value for itemId
@Component({
    template: '',
    standalone: false
})
export abstract class AddListComponent implements OnInit, OnDestroy {
  protected protectedSort: MatSort;
  protected protectedPaginator: MatPaginator;
  protectedMatSortSubscribed;
  protectedMatPaginatorSubscribed;

  @ViewChild(MatSort, { static: false })
  set matSort(ms: MatSort) {
    this.protectedSort = ms;
    if (this.protectedSort) {
      if (this.protectedMatSortSubscribed) this.protectedMatSortSubscribed.unsubscribe();
      this.protectedMatSortSubscribed = this.protectedSort.sortChange.subscribe((e) => {
        this.originalSortProtected(e.active, e.direction);
      });
    }
  }

  @ViewChild(MatPaginator, { static: false })
  set matPaginator(mp: MatPaginator) {
    this.protectedPaginator = mp;
    if (this.protectedPaginator) {
      if (this.protectedMatPaginatorSubscribed) this.protectedMatPaginatorSubscribed.unsubscribe();

      this.protectedMatPaginatorSubscribed = this.protectedPaginator.page.subscribe((e) => {
        this.service.ListRequest.pageNumber = e.pageIndex + 1;
        this.service.ListRequest.pageSize = e.pageSize;
        this.pageChangedProtected();
      });
    }
  }

  pageChangedProtected() {
    this.service.reload();
  }

  originalSortProtected(column: string, direction: string) {
    if (column) {
      this.service.setSort(column, direction);
    }
  }

  parentId: number; // the id of the parent of the children in the list. Should be assigned during component initiation. Could be from route or through input property

  @Input() protected isRowSelectable = false; // indicates whether rows can be selected and highlighted

  // protected service: IAddListService; // a service derived from ListServiceBase
  itemDeletedSubscription: Subscription;
  listReloadedSubscription: Subscription;
  blockingStateSubscription: Subscription;
  protected loadAfterInit = true; // indicates whether the list should load on init or whether that will be handled elsewhere
  protected clearFiltersOnInit = true; // parent-filtered dashboard lists can opt out

  itemList: any[] = null; // used for self managing list on a component. If null then the list on the srvice will be used

  protected defaultSortProperty = '';
  protected defaultSortDirection = '';
  protected useLocalLoadingOverlay = false;
  protected isBlocking = false;

  @BlockUI() blockUI: NgBlockUI;

  protected selectedRowId: number;

  @Output() selectedRowChanged = new EventEmitter<any>();
  @Output() itemDeleted = new EventEmitter<number>();
  @Output() listReloaded = new EventEmitter<number>();
  @Output() funkyEmit = new EventEmitter<void>();

  constructor(protected service: IAddListService) {
    // TODO: [Technical Debt][dpb][2023-02-05] Commented out the line below as the Previous fix causing all list requests to be set to the default
    //this.service.resetListRequest();
    this.itemDeletedSubscription = this.service.itemDeleted$.subscribe((item) => this.itemDeletedHandler(item));
    this.listReloadedSubscription = this.service.listReloaded$.subscribe((listItemCount) => this.listReloadedHandler(listItemCount));
    this.blockingStateSubscription = this.service.blockingState$?.subscribe((isBlocking) => {
      if (this.useLocalLoadingOverlay) {
        this.isBlocking = isBlocking;
      }
    });
  }

  protected doPostLoad() {}

  reloadList() {
    //this.blockUI.start();

    this.service
      .reload()
      .then(() => {
        //this.blockUI.stop();
      })
      .catch((error) => {
        this.stopComponentBlockUi();
      });
  }

  get sortProperty(): string {
    return this.service.ListRequest.sort.length > 0 ? this.service.ListRequest.sort[0].field : this.defaultSortProperty;
  }
  get sortDirection(): string {
    return this.service.ListRequest.sort.length > 0 ? this.service.ListRequest.sort[0].direction : this.defaultSortDirection;
  }

  ngOnInit() {
    if (this.service && this.clearFiltersOnInit) {
      this.service.ListRequest.filters = [];
    }
  }

  ngAfterViewInit() {
    if (this.loadAfterInit === true) {
      this.reloadList();
    }
  }
  ngOnDestroy() {
    this.itemDeletedSubscription.unsubscribe();
    this.listReloadedSubscription.unsubscribe();
    this.blockingStateSubscription?.unsubscribe();
    this.protectedMatSortSubscribed?.unsubscribe();
    this.protectedMatPaginatorSubscribed?.unsubscribe();
  }

  protected configureLocalLoadingOverlay() {
    this.useLocalLoadingOverlay = true;
    this.service.useBlockUi = false;
  }

  protected startComponentBlockUi() {
    if (this.useLocalLoadingOverlay) {
      this.isBlocking = true;
      return;
    }

    this.blockUI.start();
  }

  protected stopComponentBlockUi() {
    if (this.useLocalLoadingOverlay) {
      this.isBlocking = false;
      return;
    }

    this.blockUI.stop();
  }

  selectRow(row) {
    if (this.isRowSelectable === true) {
      this.selectedRowId = row.dataModel.id;
      this.selectedRowChanged.emit(row.dataModel);
      this.funkyEmit.emit();
    }
  }

  itemDeletedHandler(item: number) {
    if (item) {
      if (item === this.selectedRowId) {
        this.selectedRowId = null;
        this.selectedRowChanged.emit(null);
      }
      this.itemDeleted.emit(item);

      this.movetoPreviousPageIfFinalItemOnPage();
    }

    this.onitemDeleted(item);
  }

  // when the last item on a page is deleted we should automatically move to the previolus page
  movetoPreviousPageIfFinalItemOnPage() {
    const fullPagesFollowingDeletion = this.service.itemsCount % this.service.ListRequest.pageSize === 0;
    if (fullPagesFollowingDeletion) {
      // item deleted may have been final item on previous page
      const newPageCount = this.service.itemsCount / this.service.ListRequest.pageSize;

      if (this.service.ListRequest.pageNumber > newPageCount && newPageCount > 0) {
        this.service.ListRequest.pageNumber--;
      }
    }
  }

  onitemDeleted(item: number) {}

  onListReloaded() {}

  listReloadedHandler(listItemCount) {
    this.listReloaded.emit(listItemCount);
    this.onListReloaded();
  }

  exportList() {
    this.startComponentBlockUi();

    this.service
      .exportToExcel()
      .then(() => {
        this.stopComponentBlockUi();
      })
      .catch(() => {
        this.stopComponentBlockUi();
      });
  }
}
